

// src/pages/FormFill.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import usePageBackground from '../hooks/usePageBackground';
import { useParams } from 'react-router-dom';
import { useFadeInOnScroll } from "../theme/global";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Button
} from '@mui/material';
import * as endPoints from '../assets/js/endpoints';
import { useNotification } from '../assets/contexts/notificationContext';
import Loader from '../components/loader';
import { TimerOutlined } from '@mui/icons-material';
import '../css/global.css';
import * as globalstyles from '../theme/global';


export default function FormFill({ slug: slugProp, formularios }) {
  const [loading, setLoading] = useState(false);
  const { slug: slugParam } = useParams();
  const slug = slugProp || formularios?.slug || slugParam;
  const containerRef = useFadeInOnScroll([loading]);
  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const { showNotification } = useNotification();
  const [url, setUrl] = useState('');
  
  usePageBackground('form-background-color');

  

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    const fetchForm = async () => {
      try {
        const f = await endPoints.getFormBySlug(slug);
        setForm(f);
        const qs = f.questions || [];
        setQuestions(qs);
        const init = {};
        qs.forEach(q => {
          init[q.id] = q.tipo === 'checkbox' ? [] : null;
        });
        setAnswers(init);
      } catch (_err) {
        showNotification('Error al cargar el formulario', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [slug]);
  useEffect(() => {
    if (slug) {
      setUrl(`${window.location.origin}/form/${slug}`);
    }
  }, [slug]);

 

  const handleChange = qid => e => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setAnswers(prev => ({ ...prev, [qid]: val }));
  };


  const handleCheckboxChange = (qid, option) => e => {
    setAnswers(prev => {
      const current = Array.isArray(prev[qid]) ? prev[qid] : [];
      if (e.target.checked) {
        return { ...prev, [qid]: [...current, option] };
      }
      return { ...prev, [qid]: current.filter(o => o !== option) };

    });
  };



  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        responses: questions
          .map(q => ({
            question_id: q.id,
            respuesta: answers[q.id],
          }))
          .filter(r => {
            const resp = r.respuesta;
            return Array.isArray(resp)
              ? resp.length > 0
              : resp !== null && resp !== '';
          }),
      };

      await endPoints.submitResponsesBySlug(slug, payload);

      showNotification('¡Gracias por rellenar nuestra evaluación!', 'success');
      // si quieres redirigir:
      // navigate('/')
    } catch (_err) {
      showNotification('Error al enviar la evaluación, inténtalo de nuevo.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (!slug) return
    const subject = encodeURIComponent(`Formulario: ${form?.nombre || ''}`)
    const body = encodeURIComponent(
      `Hola,%0D%0A%0D%0AQuería compartir contigo esta evaluación:%0D%0A${url}`
    )
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }
  
  
  

  if (!form || loading) {
    return <Loader />;
  }

  return (
    <>
    <Helmet>
      <title>Formulario | CES</title>
      <meta name="description" content="Rellena el formulario para ponerte en contacto con nosotros." />
    </Helmet>
    <Container
      ref={containerRef}
      className="fade-up"
      sx={{ p: { xs: 1, sm: 2, md: 4 }, my: 4 }}
    >
      <Paper
        sx={{
          paddingY: 3,
          paddingX: { xs: '1em', sm: '2em', md: '4em' },
          marginY: { xs: '2em', md: '4em' },
          marginX: { xs: '1em', md: '6em' },
        }}
      >
        <Typography
          variant="h2"
          gutterBottom
          sx={{ color: '#80B779', fontFamily: "Great Vibes, cursive", textAlign: 'center', paddingBottom:'0.5em', paddingTop: '0.5em' }}
        >  
          {form.nombre}
        </Typography>


        {form.descripcion && (
          <Typography sx={{color: '#726B2F', fontSize:'16px !important', fontWeight: '600 !important', fontFamily: ' Nunito, san-serif', mb: 2, textAlign: 'justify' }}>
            {form.descripcion}
          </Typography>
        )}

        <Box
          component="form"
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {questions.map(q => (
            <Box key={q.id} sx={{ mb: 3, fontFamily: 'Nunito, sans-serif' }}>
              <Typography variant="subtitle1">{q.texto}</Typography>

              {q.tipo === 'texto' && (
                <TextField
                sx={{fontFamily: 'Nunito, sans-serif'}}
                  fullWidth
                  multiline
                  value={answers[q.id]}
                  onChange={handleChange(q.id)}
                />
              )}

              {q.tipo === 'numero' && (
                <TextField
                sx={{fontFamily: 'Nunito, sans-serif'}}
                  fullWidth
                  type="number"
                  value={answers[q.id]}
                  onChange={handleChange(q.id)}
                />
              )}

              {q.tipo === 'opcion_multiple' && (() => {
                let opts = [];
                if (Array.isArray(q.opciones)) {
                  opts = q.opciones;
                } else if (q.opciones) {

                  try {
                    opts = JSON.parse(q.opciones);
                  } catch (_e) {
                    opts = q.opciones.split(',').map(o => o.trim());
                  }
                }
                return (
                  <RadioGroup
                  sx={{fontFamily: 'Nunito, sans-serif'}}
                    value={answers[q.id]}
                    onChange={handleChange(q.id)}
                  >
                    {opts.map(opt => (
                      <FormControlLabel
                      sx={{fontFamily: 'Nunito, sans-serif'}}
                        key={opt}
                        value={opt}
                        control={<Radio />}
                        label={opt}
                      />
                    ))}
                  </RadioGroup>

                );
              })()}

              {q.tipo === 'checkbox' && (() => {
                let opts = [];
                if (Array.isArray(q.opciones)) {
                  opts = q.opciones;
                } else if (q.opciones) {
                  try {
                    opts = JSON.parse(q.opciones);
                  } catch (_e) {
                    opts = q.opciones.split(',').map(o => o.trim());
                  }
                }

                return (
                  <Box>
                    {opts.map(opt => (
                      <FormControlLabel
                        key={opt}
                        control={
                          <Checkbox
                            checked={answers[q.id]?.includes(opt)}
                            onChange={handleCheckboxChange(q.id, opt)}

//                 const selected = Array.isArray(answers[q.id]) ? answers[q.id] : [];

                          />
                        }
                        label={opt}
                      />
                    ))}
                  </Box>
                );
              })()}

              {q.tipo === 'booleano' && (
                <FormControlLabel
                sx={{fontFamily: 'Nunito, sans-serif'}}
                  control={
                    <Checkbox
                      checked={answers[q.id]}
                      onChange={handleChange(q.id)}
                    />
                  }
                  label="Sí / No"
                />
              )}
            </Box>
          ))}

          <Button sx={{...globalstyles.buttonStandard, cursor: 'pointer', padding: '0.6em 1.5em'}} variant="contained" onClick={handleSubmit}>
            Enviar Respuestas
          </Button>

          {form.cierre && (
            <Typography sx={{ mt: 2, textAlign: 'justify' }}>
              {form.cierre}
            </Typography>
          )}

          {form?.form_type?.name === 'Evaluación' && (
            <Button
              sx={{ ...globalstyles.buttonStandard, mt: 2, mb: 2, cursor: 'pointer', padding: '0.6em 1.5em' }}
              variant="contained"
              onClick={handleShare}
            >
              Compartir
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
    </>
  );
}

